// Centralized API helper. Always sends cookies (credentials: include).
import { logger } from '../lib/logger';

const BASE = process.env.REACT_APP_BACKEND_URL;

// ---- 401 handler registry ----
// The authenticated app shell (ProtectedRoute) registers a handler here at
// mount. Public pages (marketing, quiz, playbook) never register, so a 401 on
// those pages doesn't fire a redirect. The handler also accepts the failed
// request's `detail` string ("session_idle_timeout" / "session_absolute_timeout"
// / "session_expired" / "Invalid session") so the UI can show context.
let _on401Handler = null;
export function setOn401Handler(fn) {
  _on401Handler = typeof fn === 'function' ? fn : null;
}

async function request(path, { method = 'GET', body, headers = {} } = {}) {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  // Defensive read: a stale service worker (from a prior deployment) can
  // intercept the response, call `response.clone()` for caching, and lock the
  // body stream before we read it \u2014 producing "Failed to execute 'text' on
  // 'Response': body stream already read". When that happens we still have
  // `res.ok` / `res.status`, so for successful requests we treat the read
  // failure as a successful empty response and surface a clear hint for any
  // failure cases.
  let text = '';
  try {
    text = await res.text();
  } catch (readErr) {
    if (res.ok) {
      return null;
    }
    const err = new Error(
      `Network response was unreadable (status ${res.status}). ` +
      'Hard-refresh the page (Cmd/Ctrl+Shift+R) to clear a stale service worker, then try again.'
    );
    err.status = res.status;
    throw err;
  }
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { detail: text };
  }
  if (!res.ok) {
    // FastAPI validation errors return `detail` as a list of {loc, msg, type}
    // objects — stringify them into a human-readable single line so toasts
    // and error UIs show something useful instead of "[object Object]".
    let message;
    const detail = data && data.detail;
    if (Array.isArray(detail)) {
      message = detail
        .map((d) => {
          const field = Array.isArray(d?.loc) ? d.loc.filter((x) => x !== 'body').join('.') : '';
          const msg = d?.msg || 'invalid';
          return field ? `${field}: ${msg}` : msg;
        })
        .join('; ');
    } else if (typeof detail === 'string') {
      message = detail;
    } else if (detail) {
      message = JSON.stringify(detail);
    } else {
      message = `Request failed (${res.status})`;
    }
    const err = new Error(message);
    err.status = res.status;
    err.data = data;
    if (res.status === 401 && _on401Handler) {
      // Fire-and-forget \u2014 the handler decides whether to redirect, toast, etc.
      try {
        _on401Handler({ detail: data && data.detail, path });
      } catch (handlerErr) {
        // Handler should never throw, but if it does we don't want to mask
        // the original 401 — log to console so it's visible in devtools.
        logger.warn('[api] on401 handler threw', handlerErr);
      }
    }
    throw err;
  }
  return data;
}

export const api = {
  get: (path) => request(path),
  post: (path, body) => request(path, { method: 'POST', body }),
  patch: (path, body) => request(path, { method: 'PATCH', body }),
  put: (path, body) => request(path, { method: 'PUT', body }),
  del: (path) => request(path, { method: 'DELETE' }),
  delete: (path) => request(path, { method: 'DELETE' }),
};

// Auth endpoints
export const authApi = {
  me: () => api.get('/api/auth/me'),
  exchangeSession: (session_id, ref = null) => api.post('/api/auth/session', { session_id, ref }),
  logout: () => api.post('/api/auth/logout'),
  updateProfile: (payload) => api.patch('/api/auth/profile', payload),
  signup: (payload) => api.post('/api/auth/signup', payload),
  login: (email, password) => api.post('/api/auth/login', { email, password }),
  forgotPassword: (email) => api.post('/api/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/api/auth/reset-password', { token, password }),
  sessionPolicy: () => api.get('/api/auth/session-policy'),
};

// AI endpoints
export const aiApi = {
  usage: () => api.get('/api/ai/usage'),
  coachChat: (message, session_id = null) => api.post('/api/ai/coach/chat', { message, session_id }),
  listChatSessions: () => api.get('/api/ai/coach/sessions'),
  listChatMessages: (session_id) => api.get(`/api/ai/coach/sessions/${session_id}/messages`),
  pregameGenerate: (payload) => api.post('/api/ai/pregame/generate', payload),
  pregameHistory: () => api.get('/api/ai/pregame/history'),
  pregameDelete: (plan_id) => api.del(`/api/ai/pregame/${plan_id}`),
  journalReflect: (payload) => api.post('/api/ai/journal/reflect', payload),
  journalList: () => api.get('/api/ai/journal'),
  journalDelete: (entry_id) => api.del(`/api/ai/journal/${entry_id}`),
};

// Billing endpoints
export const billingApi = {
  plans: () => api.get('/api/billing/plans'),
  subscription: () => api.get('/api/billing/subscription'),
  checkout: (plan_key) => api.post('/api/billing/checkout', { plan_key, origin_url: window.location.origin }),
  checkoutStatus: (session_id) => api.get(`/api/billing/checkout-status/${session_id}`),
  portal: () => api.post('/api/billing/portal', { origin_url: window.location.origin }),
  pauseSubscription: (resume_in_months = null) =>
    api.post('/api/billing/pause-subscription', { resume_in_months }),
  resumeSubscription: () => api.post('/api/billing/resume-subscription'),
};

// Progress + Goals endpoints (Phase 2)
export const progressApi = {
  overview: () => api.get('/api/progress/overview'),
  weeklySummary: () => api.get('/api/progress/weekly-summary'),
};

export const goalsApi = {
  list: () => api.get('/api/goals'),
  create: (payload) => api.post('/api/goals', payload),
  update: (goal_id, payload) => api.patch(`/api/goals/${goal_id}`, payload),
  remove: (goal_id) => api.del(`/api/goals/${goal_id}`),
};

// Video library endpoints (Phase 2)
export const videosApi = {
  list: () => api.get('/api/videos'),
};

// Phase 4 — Admin endpoints (all require admin allowlist email)
export const adminApi = {
  listVideos: () => api.get('/api/admin/videos'),
  createVideo: (payload) => api.post('/api/admin/videos', payload),
  updateVideo: (video_id, payload) => api.patch(`/api/admin/videos/${video_id}`, payload),
  deleteVideo: (video_id) => api.del(`/api/admin/videos/${video_id}`),
  refreshSeedVideos: () => api.post('/api/admin/videos/refresh-seed'),
  applyVideoPreviewPolicy: (free_count = 3, locked_tier = 'pro') =>
    api.post('/api/admin/videos/apply-preview-policy', { free_count, locked_tier }),
  abResults: () => api.get('/api/admin/ab-results'),
  listReferrals: () => api.get('/api/admin/referrals'),
  updateReferral: (referral_id, payload) => api.patch(`/api/admin/referrals/${referral_id}`, payload),
  listSubscribers: () => api.get('/api/admin/subscribers'),
  subscriberQuizHistory: (email) =>
    api.get(`/api/admin/subscribers/${encodeURIComponent(email)}/quiz-history`),
  subscriberDetail: (email) =>
    api.get(`/api/admin/subscribers/${encodeURIComponent(email)}`),
  subscriberChatTranscript: (email, sessionId) =>
    api.get(`/api/admin/subscribers/${encodeURIComponent(email)}/chats/${encodeURIComponent(sessionId)}`),
  resendFrameworkEmail: (email) =>
    api.post(`/api/admin/subscribers/${encodeURIComponent(email)}/resend-framework`, {}),
  listGrants: () => api.get('/api/admin/grants'),
  createGrant: (payload) => api.post('/api/admin/grants', payload),
  revokeGrant: (grant_id) => api.del(`/api/admin/grants/${grant_id}`),
  dripStats: () => api.get('/api/admin/drip-stats'),
  recentDrips: (limit = 50) => api.get(`/api/admin/drips/recent?limit=${limit}`),
  // Private workshop decks (.pptx). Metadata only via this helper; the
  // binary download uses a raw fetch w/ credentials so the response stays a
  // Blob and can be saved to disk via browser-side ObjectURL.
  listWorkshopDecks: () => api.get('/api/admin/workshop-decks'),
};

// Phase 4 — Growth (public/auth-light)
export const eventsApi = {
  logCtaClick: (payload) =>
    fetch(`${BASE}/api/events/cta-click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include',
      keepalive: true,
    }).catch(() => {}),
};

export const referralsApi = {
  me: () => api.get('/api/referrals/me'),
};

export const marketingApi = {
  subscribe: (payload) =>
    fetch(`${BASE}/api/marketing/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).then(async (r) => {
      const text = await r.text();
      let data = null;
      try { data = text ? JSON.parse(text) : null; } catch { data = { detail: text }; }
      if (!r.ok) {
        const err = new Error((data && data.detail) || `Request failed (${r.status})`);
        err.status = r.status;
        throw err;
      }
      return data;
    }),
};

// UI helper: surface 429 (limit) errors with an Upgrade action toast.
export function handleAiError(err, toast, navigate) {
  const msg = err?.message || 'Something went wrong.';
  if (err?.status === 429) {
    toast.error(msg, {
      action: {
        label: 'Upgrade',
        onClick: () => navigate('/app/pricing'),
      },
      duration: 7000,
    });
    return;
  }
  toast.error(msg);
}
